import { createTracker, EventTypes } from 'redux-segment';

import CONF from 'config/conf.singleton';
import FLAG from 'config/flag.singleton';
import RELEASE from 'config/release.singleton';
import { USE_SEGMENT_AND_UPSCOPE } from 'config/use.conf';
import asArray from 'utils/arrays/asArray.util';
import { COGNITO_ATTRIBUTE } from 'utils/cognito.util';
import fromObjectToKeys from 'utils/converters/object/fromObjectToKeys.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isNotFn from 'utils/predicates/function/isNotFn.util';

const L = logger('SEGMENT');

const loadUpscope = (conf) => {
  L.debug('Load Upscope', conf.upscopeUse);
  if (!conf.upscopeUse) return;

  // code provided as is with all its quirks left untouched
  /* eslint-disable */
  (function (w, u, d) {
    var i = function () {
      i.c(arguments);
    };
    i.q = [];
    i.c = function (args) {
      i.q.push(args);
    };
    var l = function () {
      var s = d.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = 'https://code.upscope.io/qPYBL4bhqo.js';
      var x = d.getElementsByTagName('script')[0];

      x.parentNode.insertBefore(s, x);
    };
    if (typeof u !== 'function') {
      w.Upscope = i;
      l();
    }
  })(window, window.Upscope, document);

  window.Upscope('init', {
    // Don't initialise here, we do updateConnection when we are logged in
  });
  /* eslint-enable */
};

const trackPageLoad = (conf) => {
  // NOTE: We don't need to call this as it's done automatically by the analytics.js script
  if (!USE_SEGMENT_AND_UPSCOPE) {
    L.warn('trackPageLoad()', 'Should not be called due to USE_SEGMENT_AND_UPSCOPE:', USE_SEGMENT_AND_UPSCOPE);
    return;
  }

  // @SEE: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/ajs-classic/#page
  const analytics = window ? window.analytics : undefined;
  if (analytics) {
    L.debug('trackPageLoad()', 'segmentWriteKey:', conf.segmentWriteKey);
    analytics.page();
  } else {
    L.warn('trackPageLoad()', 'Missing window.analytics:', analytics);
  }
};

const identify = ({ id, traits, options }) => {
  // @SEE: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/ajs-classic/#identify

  const analytics = window ? window.analytics : undefined;
  if (!analytics) {
    L.warn('identify()', 'Missing window.analytics:', analytics);
    return;
  }

  L.debug('identify()', { id, traits, options });
  if (typeof analytics.load === 'function') {
    // NOTE: this should somehow make things better, but may not exist in this old library
    analytics.load(CONF.segmentWriteKey);
  } else {
    L.warn('identify()', 'Invalid function window.analytics.load:', analytics.load);
  }

  analytics.identify(id, traits, options);
};

const setCompany = (data) => {
  const analytics = window ? window.analytics : undefined;
  if (!analytics) {
    L.warn('setCompany()', 'Missing window.analytics:', analytics);
    return;
  }

  const { id, name, username } = asObject(data);
  if (!id) {
    L.warn('setCompany()', 'No id:', id);
  }

  const traits = { company: { id, name } };
  const options = { integrations: { All: false, Intercom: true } };

  L.debug('setCompany()', { username, id, name, traits, options });

  // NOTE: no sense in calling analytics.identify(traits, options) if no usename present
  if (username) analytics.identify(username, traits, options);
};

const upscopeUpdateConnection = (options) => {
  const FTAG = 'upscopeUpdateConnection()';

  if (!CONF.upscopeUse) return void L.debug(FTAG, 'Not enabled CONF.upscopeUse:', CONF.upscopeUse);
  if (isNotFn(window.Upscope)) return void L.warn(FTAG, 'Not a function window.Upscope:', window.Upscope);

  return window.Upscope('updateConnection', { identities: options?.identities, uniqueId: options?.uniqueId });
};

const trackLocationChange = (getState, action) => ({
  eventType: EventTypes.page,
  eventPayload: { name: action.payload.location.pathname },
});

const trackPostSalonDataSuccess = (getState, action) => {
  if (!action?.response) return;

  const { name, id } = action.response ?? {};
  SEGMENT.setCompany({ id, name });
};

const trackCognitoLogout = () => {
  const analytics = window ? window.analytics : undefined;
  if (analytics) {
    L.info('cognitoLogoutReducer()', 'Logging out and resetting analytics...');
    analytics.reset();
  }
  upscopeUpdateConnection({ identities: null, uniqueId: null });
  return [{ eventType: EventTypes.reset }];
};

const trackCognitoLogin = (getState, action) => {
  const FTAG = 'trackCognitoLogin()';

  const { email, name, sub: id, phone_number: phone } = action?.attributes ?? {};

  /** @type string[] */
  const managedSalonsAttribute = action?.attributes?.[COGNITO_ATTRIBUTE.salonsManage];
  const jsonManagedSalons = managedSalonsAttribute ? JSON.parse(managedSalonsAttribute) : null;
  const managedSalons = asArray(jsonManagedSalons);

  /** @type string[] */
  const salonsWithAccessAttribute = action?.attributes?.[COGNITO_ATTRIBUTE.salonsAccess];
  const jsonSalonsWithAccess = salonsWithAccessAttribute ? JSON.parse(salonsWithAccessAttribute) : null;
  const salonsWithAccess = asArray(jsonSalonsWithAccess);

  if (!id) return {};

  const salonId = window.location.hash.match(/#\/salon\/(\d+)/)?.[1] ?? null;
  if (!salonId) return {};
  const salonIdNumber = Number(salonId);
  const isManager = managedSalons.includes(salonIdNumber);
  const isRegular = salonsWithAccess.includes(salonIdNumber);
  const hasAccessToSalon = salonIdNumber && (isManager || isRegular);

  if (hasAccessToSalon) {
    L.info(FTAG, 'Attempting to identify user with id:', id, ' because:', { salonIdNumber, isManager, isRegular });
    RELEASE.display()
      .then((version) => {
        const traits = { email, name, phone, version };
        L.debug(FTAG, 'Identifying user:', id, ' with traits:', traits);
        SEGMENT.identify({ id, traits, options: { integrations: { All: false, Intercom: true } } });
      })
      .catch((e) => L.warn(FTAG, e));
  } else {
    L.info(FTAG, 'No access to salon:', { salonIdNumber, isManager, isRegular });
  }

  upscopeUpdateConnection({ identities: [name], uniqueId: email });

  // also let redux-segment handle this, but with just userId
  return [{ eventType: EventTypes.identify, eventPayload: { userId: id } }];
};

const TRACKER_MAP = {
  '@@router/LOCATION_CHANGE': trackLocationChange,
  POST_SALON_DATA_SUCCESS: trackPostSalonDataSuccess,
  COGNITO_LOGOUT: trackCognitoLogout,
  COGNITO_LOGIN: trackCognitoLogin,
};

const TRACKER_KEYS = fromObjectToKeys(TRACKER_MAP);

// noinspection JSCheckFunctionSignatures
const normalReduxSegmentTracker = createTracker({ mapper: TRACKER_MAP });

const strictReduxSegmentTracker = (store) => (next) => (action) =>
  TRACKER_KEYS.includes(action?.type) ? normalReduxSegmentTracker(store)(next)(action) : next(action);

const init = (conf) => {
  loadUpscope(conf);
  if (USE_SEGMENT_AND_UPSCOPE) {
    // this is a kind of init done once on every page load
    trackPageLoad(conf);
  } else {
    L.debug('init()', 'USE_SEGMENT_AND_UPSCOPE:', USE_SEGMENT_AND_UPSCOPE, 'skipping trackPageLoad()');
  }
};

const SEGMENT = Object.freeze({
  identify,
  init,
  loadUpscope,
  reduxTracker: FLAG.trackBeCalls ? normalReduxSegmentTracker : strictReduxSegmentTracker,
  setCompany,
});

// noinspection JSUnusedGlobalSymbols
export default SEGMENT;
